import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="autogrow"
// Dual-mode:
//   * Plain <textarea> (chat composer): grows from 1 line up to max-rows (default 5).
//   * Trix editor (task form / comments): keeps a comfortable starting height and
//     only grows; never collapses to a single line.
// Legacy support: data-autogrow-height-value sets a minimum starting height in px.
export default class extends Controller {
  static targets = ["field"]
  static values  = {
    maxRows: { type: Number, default: 5 },
    height:  { type: Number, default: 0 }
  }

  connect() {
    this._ready = false
    const el = this._editableEl()

    if (!el) return

    if (el.tagName === "TEXTAREA") {
      // Plain textarea: initialise immediately
      this._init()
    } else if (el.editor) {
      // Trix already initialised (e.g. Turbo cache restore)
      this._init()
    } else {
      this._onTrixInit = () => this._init()
      this.element.addEventListener("trix-initialize", this._onTrixInit, { once: true })
    }
  }

  disconnect() {
    if (this._onTrixInit) this.element.removeEventListener("trix-initialize", this._onTrixInit)
    if (this._onChange) {
      this.element.removeEventListener("trix-change", this._onChange)
      this._editableEl()?.removeEventListener("input", this._onChange)
    }
  }

  // Called by data-action="input->autogrow#resize" on the textarea
  resize() {
    this._grow()
  }

  // Legacy alias kept for older views still using "input->autogrow#grow"
  grow() {
    this._grow()
  }

  // Called externally after message send to collapse back to the starting height
  reset() {
    const el = this._editableEl()
    if (!el) return
    el.style.height    = `${this._minHeight}px`
    el.style.overflowY = "hidden"
  }

  _init() {
    if (this._ready) return
    this._ready = true

    const el = this._editableEl()
    if (!el) return

    const isTrix     = el.tagName === "TRIX-EDITOR"
    const cs         = window.getComputedStyle(el)
    const lineHeight = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.5
    const padV       = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom)
    const cssMinHeight = parseFloat(cs.minHeight) || 0

    // Textareas are capped to max-rows; Trix editors grow unbounded.
    this._maxHeight = isTrix ? Infinity : lineHeight * this.maxRowsValue + padV

    el.style.overflowY = "hidden"

    // height:auto collapses <textarea> in some browsers — measure with height:0 instead.
    let contentHeight
    if (isTrix) {
      el.style.minHeight = "0"
      el.style.height    = "auto"
      contentHeight = el.scrollHeight
    } else {
      el.style.height = "0"
      contentHeight = el.scrollHeight
    }

    this._minHeight = Math.max(this.heightValue || 0, cssMinHeight, contentHeight)
    el.style.height = `${Math.max(this._minHeight, contentHeight)}px`

    this._onChange = () => this._grow()
    this.element.addEventListener("trix-change", this._onChange)
    el.addEventListener("input", this._onChange)
  }

  _grow() {
    const el = this._editableEl()
    if (!el) return

    const isTrix = el.tagName === "TRIX-EDITOR"
    el.style.height = isTrix ? "auto" : "0"
    const needed       = Math.max(el.scrollHeight, this._minHeight || 0)
    const capped       = Math.min(needed, this._maxHeight)
    el.style.height    = `${capped}px`
    el.style.overflowY = needed > this._maxHeight ? "auto" : "hidden"
  }

  _editableEl() {
    // 1. direct target (data-autogrow-target="field")
    if (this.hasFieldTarget) return this.fieldTarget

    // 2. wraps a trix-editor
    if (this.element.tagName === "TRIX-EDITOR") return this.element
    const trix = this.element.querySelector("trix-editor")
    if (trix) return trix

    // 3. is or wraps a textarea
    if (this.element.tagName === "TEXTAREA") return this.element
    const ta = this.element.querySelector("textarea")
    if (ta) return ta

    return null
  }
}
